/**
 * @file handleVolPagesResponse.js
 * @description vol/all：用 unlocked/accessStatus 分流；兼容旧 10701/10702
 * @module features/comic/utils/handleVolPagesResponse
 */

import { ERR_VOL_NEED_LOGIN, ERR_VOL_NEED_PAY, normalizeApiCode } from '@/shared/utils/apiErrorCodes'
import { VOL_ACCESS_STATUS, resolveVolAccessStatus } from './volAccess'

/**
 * @param {unknown} code
 * @returns {'ok'|'need-login'|'need-pay'|'error'}
 */
export function resolveVolPagesResult(code) {
  const n = normalizeApiCode(code)
  if (n === 0 || n === 200) return 'ok'
  if (n === ERR_VOL_NEED_LOGIN) return 'need-login'
  if (n === ERR_VOL_NEED_PAY) return 'need-pay'
  return 'error'
}

/**
 * 解析 vol/all data 的访问态。有 accessStatus 优先；否则用 unlocked 兜底。
 * 两字段都缺时视为可阅读（兼容改造前成功响应）。
 * @param {unknown} payload
 * @param {{ hasToken?: boolean }} [options]
 * @returns {import('./volAccess').VolAccessStatus}
 */
export function resolveVolPagesAccess(payload, options = {}) {
  const data = payload && typeof payload === 'object' ? payload : {}
  const hasToken = Boolean(options.hasToken)
  if (data.accessStatus) {
    return resolveVolAccessStatus(
      { accessStatus: data.accessStatus },
      { hasToken }
    )
  }
  if (data.unlocked === false) {
    return hasToken ? VOL_ACCESS_STATUS.LOCKED : VOL_ACCESS_STATUS.NEED_LOGIN
  }
  if (data.unlocked === true) {
    return VOL_ACCESS_STATUS.UNLOCKED
  }
  return VOL_ACCESS_STATUS.UNLOCKED
}

/**
 * 免费或已解锁才可分页阅读、拼进邻卷流
 * @param {unknown} payload
 * @param {{ hasToken?: boolean }} [options]
 * @returns {boolean}
 */
export function isVolPagesReadable(payload, options = {}) {
  const status = resolveVolPagesAccess(payload, options)
  return status === VOL_ACCESS_STATUS.FREE || status === VOL_ACCESS_STATUS.UNLOCKED
}

/**
 * 清空阅读器页图与邻卷缓冲，保留 Toolbar / 目录壳。
 * 仅用于 10701 等需空白拦截的场景；未解锁请用 applyLockedVolPreviewImages。
 * @param {object} ctx
 */
export function clearReaderPageImages(ctx) {
  ctx.pages = []
  ctx.nextVolumePages = []
  ctx.loadedVolumes = []
  ctx.prevVolumePages = []
  ctx.loadedPrevVolumes = []
  ctx.imageRefs = []
  ctx.hasNext = false
  ctx.hasPrev = false
  ctx.isCurrentVolumeComplete = false
  ctx.showLoadNextVolumePrompt = false
  ctx.showLoadPrevVolumePrompt = false
  ctx.pagesLocked = false
}

/**
 * 未解锁：只留第一张预览图、锁分页、清空邻卷，不拉互动。
 * @param {object} ctx
 * @param {unknown} payload vol/all 的 data
 */
export function applyLockedVolPreviewImages(ctx, payload) {
  const data = payload && typeof payload === 'object' ? payload : {}
  const imgUrls = Array.isArray(data.imgUrls) ? data.imgUrls : []
  ctx.pages = imgUrls.length ? [imgUrls[0]] : []
  ctx.imageRefs = []
  ctx.nextVolumePages = []
  ctx.loadedVolumes = []
  ctx.prevVolumePages = []
  ctx.loadedPrevVolumes = []
  ctx.hasNext = false
  ctx.hasPrev = false
  ctx.isCurrentVolumeComplete = false
  ctx.showLoadNextVolumePrompt = false
  ctx.showLoadPrevVolumePrompt = false
  ctx.interactData = null
  ctx.pagesLocked = true
  if (data.volId) ctx.volId = data.volId
}
